// English strings for the redesigned Settings pages (owner, 2026-10-22: "work on settings like you do
// on online store"). Spread into `en` as `en.settings`, so keys read `t("settings.…")`.
// Copy rules: DESIGN_SYSTEM §9.6 (plain words, sentence case, no exclamation marks).
export const settingsStrings = {
  kit: {
    unsaved: "You have unsaved changes",
    discard: "Discard",
    save: "Save",
    saving: "Saving…",
    saved: "Saved",
    cancel: "Cancel",
    done: "Done",
    add: "Add",
    edit: "Edit",
    remove: "Remove",
    moveUp: "Move up",
    menuFor: "More for {name}",
    chars: "{n}/{max}",
    hours: "hours",
    days: "days",
    points: "points",
    on: "On",
    off: "Off",
    conflict: "Someone else changed these settings. Reload to see them.",
  },
  checkout: {
    lead: "What customers fill in at checkout, the rules checked on every order and deposits on cash on delivery.",
    tabs: { form: "Checkout form", protection: "Order protection", deposits: "Partial payment" },
    who: {
      title: "Who can check out",
      help: "Let anyone order, or ask customers to sign in first.",
      guest: "Anyone (guest checkout)",
      account: "Signed-in customers only",
      guestNote:
        "Most stores in Nepal sell more with guest checkout: customers order with a name and phone number.",
      accountNote:
        "Customers sign in with a code sent to their phone or email before they can order.",
    },
    fields: {
      title: "Fields on the form",
      help: "Name, phone and the delivery area are always asked.",
      email: "Email address",
      landmark: "Landmark or tole",
      phoneAlt: "Second phone number",
      phoneAltHint: "Useful when the courier can't reach the first one",
      note: "Order note",
      noteHint: "Anything the customer wants to tell you",
      gift: "Gift message",
      giftHint: "Printed on the packing slip",
      date: "Preferred delivery date",
      dateHint: "The customer picks a day",
      b2b: "Company invoice",
      b2bHint: "Company name and PAN for the invoice",
      promo: "Coupon code box",
      promoHint: "Hide it when you run no coupons",
    },
    rules: { hidden: "Hidden", optional: "Optional", required: "Required" },
    custom: {
      title: "Your own questions",
      help: "Up to 6 extra questions. Answers are saved on the order.",
      add: "Add question",
      edit: "Edit question",
      full: "You can ask up to {n} questions.",
      empty: "No extra questions. Start from one of these:",
      label: "Question",
      type: "Answer type",
      options: "Choices",
      optionsHelp: "One per line, up to 10.",
      helpText: "Hint under the question",
      required: "Customers must answer",
      requiredHint: "The order can't be placed without an answer",
      labelNeeded: "Write the question.",
      optionsNeeded: "Add at least one choice.",
      types: {
        text: "Short text",
        textarea: "Long text",
        select: "Choice",
        checkbox: "Tick box",
        date: "Date",
      },
      ideas: {
        size: "Your size (for tailoring)",
        howHeard: "How did you hear about us?",
        giftWrap: "Wrap it as a gift",
        deliveryTime: "Best time to deliver",
      },
      ideaOptions: {
        howHeard: "Facebook|Instagram|TikTok|A friend|Google",
        deliveryTime: "Morning|Afternoon|Evening",
      },
    },
    order: {
      title: "Order rules",
      help: "Limits and charges applied to every online order.",
      min: "Minimum order",
      minHelp: "Leave empty for no minimum.",
      codFee: "Cash on delivery charge",
      codFeeHelp: "Added when the customer pays on delivery.",
      sla: "Dispatch promise",
      slaHelp: "Orders not sent within this time are flagged as late.",
      whatsapp: "WhatsApp for orders",
      whatsappHelp: 'Shows an "Order on WhatsApp" button. Use the +977 format.',
      whatsappPh: "+9779800000000",
    },
    finish: {
      title: "Terms and thank-you note",
      help: "The last things customers see.",
      terms: "Ask customers to accept your terms",
      termsHint: "A tick box they must tick before placing the order",
      termsText: "Tick box text",
      termsDefault: "I agree to the store's terms and return policy.",
      thanks: "Thank-you note",
      thanksPh: "Thank you for shopping with us. We'll call you to confirm your order.",
    },
    preview: {
      title: "Live preview",
      optional: "optional",
      signIn: "Sign in to continue",
      contact: "Contact",
      name: "Full name",
      phone: "Phone number",
      address: "Delivery address",
      district: "District",
      area: "Area",
      more: "A few more questions",
      promo: "Coupon code",
      apply: "Apply",
      place: "Place order",
      thanks: "Thank-you page",
    },
  },
  protection: {
    tiles: {
      rules: "Rules on",
      rulesHint: "{n} rules in total",
      blocked: "Blocked",
      blockedHint: "Phones and emails that can't order",
      trusted: "Trusted",
      trustedHint: "Skip the checks below",
    },
    rules: "Rules on every order",
    rulesHelp:
      "Checked when an online order is placed. Orders that break a rule are stopped with your message.",
    add: "Add rule",
    empty: "No rules yet. Start with one of these:",
    quick: "Quick add:",
    says: "Customers see: {message}",
    removed: "Rule removed",
    added: "Rule added",
    removeTitle: "Remove this rule?",
    amount: "Amount",
    overAmount: "For orders over",
    count: "Count",
    items: "Most items",
    orders: "Most orders",
    itemsUnit: "items",
    ordersUnit: "orders",
    period: "In",
    needAmount: "Enter an amount.",
    needCount: "Enter a number above 0.",
    message: "Message to the customer",
    messageHelp: "Shown when the order is stopped. Leave empty for the default.",
    result: "The rule",
    say: {
      max_amount: "Orders up to {amount}",
      cod_cap: "Cash on delivery up to {amount}",
      require_otp: "Phone code for every COD order",
      require_otp_over: "Phone code for COD over {amount}",
      per_phone_period: "{count} orders per phone in {hours} hours",
      max_qty: "Up to {count} items per order",
    },
    explain: {
      max_amount: "Larger orders are stopped. Useful while you build trust with new customers.",
      cod_cap: "Larger orders must be paid online. Cash on delivery stays open for smaller ones.",
      require_otp: "The customer types a code sent to their phone before a COD order is placed.",
      per_phone_period: "Stops one phone number placing many orders in a short time.",
      max_qty: "Stops bulk buying of one order, for example during a sale.",
    },
    messages: {
      max_amount: "This order is above our online limit. Please call us to place it.",
      cod_cap: "For orders this large, please pay online.",
      require_otp: "We sent a code to your phone to confirm this order.",
      per_phone_period: "You've placed several orders recently. Please call us to order more.",
      max_qty: "You can order up to this many items at once.",
      require_deposit: "A deposit is needed for this order.",
    },
    lists: {
      block: "Blocked customers",
      blockHelp: "These phones and emails can't place online orders.",
      blockEmpty: "Nobody is blocked.",
      allow: "Trusted customers",
      allowHelp: "These customers skip the limits and the phone code.",
      allowEmpty: "No trusted customers yet.",
      kind: "Type",
      phone: "Phone",
      email: "Email",
      phonePh: "98XXXXXXXX",
      emailPh: "name@example.com",
      reason: "Reason (only you see it)",
    },
  },
  deposits: {
    title: "Deposit rules",
    tiles: {
      rules: "Rules on",
      rulesHint: "{n} rules in total",
      example: "Example order",
      exampleHint: "With Rs 150 delivery",
      payNow: "Paid before dispatch",
      payNowHint: "The largest deposit your rules ask",
      none: "No deposit is asked",
    },
    split: "On a Rs 5,000 order: {now} now, {later} on delivery",
    removeTitle: "Remove this deposit rule?",
    how: "How customers pay",
    steps: {
      cart: "Places a COD order",
      cartBody: "The rules decide if a deposit is needed.",
      pay: "Pays the deposit online",
      payBody: "With the payment methods you've turned on.",
      ship: "You dispatch",
      shipBody: "The order shows the deposit as paid.",
      collect: "Courier collects the rest",
      collectBody: "Only the balance is cash on delivery.",
    },
    note: "Deposits need an online payment method. Turn one on in Apps → Payments.",
  },
  loyalty: {
    lead: "Reward customers who come back: points on paid orders, member levels and rewards they can spend.",
    tabs: { overview: "Overview", programme: "Programme", members: "Members" },
    noTier: "Member",
    live: "Live",
    paused: "Paused",
    summary: "Earn {earn} per Rs 100 spent · 1 point = {value} · pay up to {max}% of an order",
    editProgramme: "Edit programme",
    pause: "Pause",
    turnOn: "Turn on",
    turnedOn: "Loyalty is on. Customers now earn points on paid orders.",
    turnedOff: "Loyalty is paused. Customers keep their points.",
    fixFirst: "Fix the highlighted fields first.",
    tiles: {
      members: "Members",
      active: "{n} active in the last 30 days",
      outstanding: "Points not yet spent",
      outstandingHint: "Across all members",
      liability: "What those points are worth",
      liabilityHint: "If every member spent them today",
      redeemed: "Spent in 30 days",
      earned: "{n} earned in 30 days",
    },
    levels: {
      title: "Member levels",
      help: "Members move up as they spend more.",
      editHelp:
        "Up to 5 levels. Higher levels earn points faster. The first level is for everyone.",
      none: "No levels. Everyone earns at the same rate.",
      add: "Add level",
      full: "You can have up to 5 levels.",
      newName: "Level {n}",
      name: "Level",
      spend: "From total spend",
      multiplier: "Points speed",
      perk: "Perk shown to members",
      perkPh: "For example: free gift wrap",
      from: "from {amount} spent",
      everyone: "Everyone",
      times: "{x}× points",
      problem: "Each level needs a name and a higher spend than the one before.",
    },
    rewards: {
      title: "Rewards",
      help: "What members can spend their points on.",
      editHelp: "Members pick a reward on their account page or at checkout.",
      none: "No rewards yet. Members can still spend points as money off.",
      add: "Add reward",
      edit: "Edit reward",
      kind: "Reward",
      kinds: {
        amount_off: "Money off",
        percent_off: "Percent off",
        free_delivery: "Free delivery",
        gift: "Free gift",
      },
      amount: "Money off",
      percent: "Percent off",
      points: "Costs",
      spent: "Worth {amount} in points",
      who: "Who can use it",
      everyone: "Every member",
      tierUp: "{tier} and above",
      name: "Name customers see",
      nameHelp: "Leave empty to use the description.",
      giftNote: "You choose and pack the gift. The order shows which reward was used.",
      needPoints: "Set how many points it costs.",
      cost: "{n} points",
      saysAmount: "{amount} off",
      saysPercent: "{percent}% off",
      saysDelivery: "Free delivery",
      saysGift: "A free gift",
      hide: "Hide",
      show: "Show",
    },
    card: {
      title: "What members see",
      balance: "Your points",
      points: "{n} points",
      worth: "Worth {amount}",
      memberNo: "Member number",
      sampleNo: "HP-1024",
      ready: "Ready",
      more: "{n} more",
      note: "Shown on the customer's account page. The member number works at your shop counter too (POS).",
    },
    basics: {
      title: "The programme",
      help: "Its name and the welcome gift.",
      name: "Programme name",
      nameHelp: 'For example "Himal Points" or "Club Rewards".',
      active: "Programme is on",
      activeHint: "When off, nobody earns or spends points. Balances are kept.",
      welcome: "Welcome points",
      welcomeHelp: "Given once, with the first paid order.",
      expiry: "Points expire",
      expiryHelp: "Unspent points older than this are removed.",
      never: "Never",
      afterDays: "After {n} days",
    },
    money: {
      title: "Earning and spending",
      help: "Points are earned when an order is paid and taken back if it is returned.",
      earn: "Points earned",
      per100: "per Rs 100",
      earnHelp: "On the items, after discounts.",
      value: "1 point is worth",
      valueHelp: "When spent at checkout.",
      min: "Spend from",
      minHelp: "The fewest points a member can spend at once.",
      max: "Most of an order paid with points",
      maxHelp: "Keeps part of every order paid in money.",
      delivery: "Earn on the delivery charge too",
      deliveryHint: "Off: points are earned on the items only",
      example: "A {order} order earns {points} points, worth {value} on the next order.",
    },
    members: {
      search: "Search members",
      searchPh: "Name, phone or member number",
      count: "{n} members",
      empty: "No members yet",
      emptyBody: "Customers become members with their first paid order while the programme is on.",
      cols: {
        member: "Member",
        number: "Member no.",
        level: "Level",
        points: "Points",
        spend: "Total spent",
        last: "Last activity",
      },
      adjust: "Add or take away points",
      add: "Add",
      take: "Take",
      why: "Why (the customer sees this)",
      adjusted: "Points updated",
      confirmAdd: "Add {n} points?",
      confirmTake: "Take away {n} points?",
      kinds: {
        earn: "Earned",
        welcome: "Welcome points",
        redeem: "Spent",
        reverse: "Order returned",
        expire: "Expired",
        adjust: "Changed by you",
      },
    },
  },
  digital: {
    lead: "Sell files, licence keys and links. Customers get them as soon as the order is paid.",
    add: "Make a product digital",
    removed: "The product is a normal product again",
    makePhysical: "Make it a normal product",
    makePhysicalTitle: "Make {name} a normal product?",
    makePhysicalBody: "It will need delivery again. Orders already paid keep their downloads.",
    times: "times",
    mb: "{n} MB",
    kb: "{n} KB",
    kinds: {
      file: "File",
      fileBody: "PDF, e-book, music, design files",
      key: "Licence key",
      keyBody: "Game codes, software keys, gift codes",
      link: "Link",
      linkBody: "A course, a folder or a private page",
    },
    tiles: {
      products: "Digital products",
      productsHint: "Delivered without a courier",
      keys: "Keys in stock",
      keysHint: "{n} already sent",
      deliveries: "Deliveries",
      deliveriesHint: "Paid orders with something to download",
      downloads: "Downloads",
      downloadsHint: "Across all deliveries",
    },
    list: {
      title: "Your digital products",
      help: "What each product delivers after payment.",
      emptyTitle: "No digital products yet",
      emptyBody:
        "Pick any product and choose what the customer gets: a file, a licence key or a link.",
      files: "Files: {n}",
      keysLeft: "Keys left: {n}",
      sent: "Sent: {n}",
    },
    keys: {
      manage: "Keys",
      available: "In stock ({n})",
      delivered: "Sent ({n})",
      reveal: "Show the key",
      add: "Add {n} keys",
      empty: { available: "No keys in stock. Paste more below.", delivered: "No keys sent yet." },
      note: "Keys are stored encrypted. Each one is sent to one customer only, after payment.",
    },
    settings: {
      title: "Delivery settings",
      help: "How long links last and how customers get them.",
      enabled: "Sell digital products",
      enabledHint: "Off: digital products can't be bought",
      limit: "Downloads per order",
      expiry: "Links last",
      email: "Email the links",
      emailHint: "Sent when the payment is confirmed",
      thanks: "Show on the thank-you page",
      thanksHint: "Customers can download right away",
      note: "Note for customers",
      notePh: "Need help? Message us on WhatsApp with your order number.",
    },
    preview: {
      title: "What the customer gets",
      paid: "Payment received",
      yours: "Your downloads",
      file: "Weaving guide.pdf",
      left: "{n} downloads · link works for {days} days",
      download: "Download",
      key: "HMLT-7Q2K-91XZ-PA4M",
      copy: "Copy",
      email: "The same links are emailed and stay on the order page.",
      noEmail: "Links stay on the thank-you and order pages. No email is sent.",
    },
    deliveries: {
      title: "Recent deliveries",
      help: "Every paid order with something to download.",
      search: "Search deliveries",
      searchPh: "Order, customer or product",
      empty: "Nothing delivered yet.",
      never: "Never",
      cols: {
        order: "Order",
        product: "Product",
        used: "Downloads",
        expires: "Link expires",
        state: "Status",
      },
      states: {
        active: "Active",
        expired: "Expired",
        revoked: "Stopped",
        waiting_key: "Waiting for a key",
      },
      resend: "Send the links again",
      reset: "Give fresh downloads",
      revoke: "Stop the links",
      done: {
        resend: "Links sent again",
        reset: "Downloads reset with a new expiry",
        revoke: "Links stopped",
      },
    },
    form: {
      editTitle: "Edit digital product",
      product: "Product",
      searchPh: "Search your products",
      change: "Change",
      what: "The customer gets",
      drop: "Upload files",
      dropHelp: "Up to 5 files, 100 MB each. Only paying customers can download them.",
      uploading: "Uploading…",
      tooBig: "{name} is larger than 100 MB.",
      limit: "Downloads per order",
      limitHelp: "Leave empty to use the store setting.",
      link: "Link",
      linkHelp: "Shown only after payment. Must start with https://",
      linkPh: "https://",
      keys: "Licence keys",
      moreKeys: "Add keys",
      keysHelp: "One per line. {n} keys ready.",
      keysPh: "AAAA-BBBB-CCCC-DDDD",
      instructions: "How to use it",
      instructionsPh: "Open the file on any phone or computer.",
      pickProduct: "Pick a product.",
      needFile: "Upload at least one file.",
      needLink: "Enter a link starting with https://",
      needKeys: "Paste at least one key.",
      savedKeys: "Saved. {n} keys added, {dup} already there.",
    },
  },
  region: {
    lead: "How your store shows dates, and the markets you sell to.",
    tabs: { region: "Language and region", markets: "Markets" },
    language: {
      title: "Store language",
      help: "The language of your store pages, emails and invoices.",
      en: "English",
      enHelp: "On now",
      ne: "नेपाली (Nepali)",
      neHelp: "Coming later",
    },
    calendar: {
      title: "Calendar",
      help: "The dates customers see on your store, invoices and messages.",
      bs: "Bikram Sambat (BS)",
      ad: "Gregorian (AD)",
      bsNote:
        "Saved. Store pages show Bikram Sambat dates, for example 6 Kartik 2083, once the BS calendar is switched on; until then they show AD dates.",
      adNote: "Dates show in the Gregorian calendar, for example 22 Oct 2026.",
    },
    fixed: {
      title: "Fixed for Nepal",
      help: "These follow Nepal and can't be changed yet.",
      timezone: "Time zone",
      timezoneValue: "Nepal time (UTC+5:45)",
      currency: "Currency",
      currencyValue: "Nepali rupee (Rs)",
      phone: "Phone numbers",
      phoneValue: "+977",
      weight: "Weights",
      weightValue: "Kilograms (kg)",
    },
    preview: {
      title: "How customers see it",
      product: "Dhaka topi",
      stock: "{n} in stock",
      add: "Add to cart",
      order: "Order summary",
      number: "Order",
      placed: "Placed",
      total: "Total",
      bsDate: "6 Kartik 2083",
      note: "A sample. Your store shows your own products and orders.",
    },
  },
  markets: {
    nepal: "Nepal",
    primary: "Main market",
    live: "Selling now",
    nepalHelp: "Every product, price, delivery area and payment method on your store is for Nepal.",
    npr: "Nepali rupee (Rs)",
    basePrices: "Your product prices",
    allDistricts: "Your delivery areas",
    nepalPayments: "COD, eSewa, Khalti, Fonepay",
    open: "Open",
    rows: { currency: "Currency", prices: "Prices", delivery: "Delivery", payments: "Payments" },
    later: "Coming later",
    abroad: {
      title: "Sell to other countries",
      help: "What selling abroad will add when it arrives.",
      currency: "Their currency",
      currencyBody: "Prices shown in the customer's money.",
      prices: "Prices per country",
      pricesBody: "Set your own price or round the converted one.",
      delivery: "International delivery",
      deliveryBody: "Couriers and charges for each country.",
      payments: "Card payments",
      paymentsBody: "So customers abroad can pay online.",
      note: "Nothing to set up now. Your store sells in Nepal in Nepali rupees.",
    },
    deliveryAreas: "Delivery areas and charges",
    paymentMethods: "Payment methods",
  },
  taxes: {
    lead: "VAT, your tax rates and what every invoice shows.",
    tiles: {
      vat: "VAT",
      registered: "Registered",
      notRegistered: "Not registered",
      rate: "{n}% on standard items",
      noVat: "No VAT is added",
      prices: "Your prices",
      asIs: "As you set them",
      include: "Include VAT",
      exclude: "Plus VAT",
      pricesHint: "How the price on the product is read",
      invoices: "Invoices",
      invoiceKind: "Simple invoice",
      invoicesHint: "Not an IRD-certified tax invoice",
    },
    vat: {
      title: "VAT registration",
      help: "Turn this on only if your business is registered for VAT with the IRD.",
      registered: "VAT registered",
      notRegistered: "Not registered",
      pan: "PAN / VAT number",
      panPh: "9 digits",
      panHelp: "Printed on invoices.",
      panBad: "A PAN has 9 digits.",
      panFromProfile: "Your business profile has {pan}.",
      usePan: "Use it",
      include: "Prices include VAT",
      includeHint: "Off: VAT is added on top at checkout",
      delivery: "VAT on the delivery charge",
      deliveryHint: "Add VAT to delivery too",
      exampleIncl: "A {price} price is {net} plus {vat} VAT. The customer pays {price}.",
      exampleExcl: "A {price} price gets {vat} VAT added. The customer pays {total}.",
      notRegisteredNote:
        "No VAT is added and invoices show no VAT line. Most small online shops in Nepal are not VAT registered.",
    },
    rates: {
      title: "Tax rates",
      help: "Each product uses one of these. New products use Standard.",
      label: "Name on invoices",
      rate: "Rate",
      zero: "Always 0%",
      yours: "Edited",
      default: "Default",
      note: "Nepal's VAT is 13%. Change it only if the IRD changes the rate.",
      kinds: {
        standard: "Standard",
        standardHelp: "Most products",
        exempt: "Exempt",
        exemptHelp: "No VAT by law",
        zero: "Zero-rated",
        zeroHelp: "Exports and similar",
      },
    },
    invoice: {
      title: "Invoice details",
      help: "What every invoice and receipt shows.",
      pan: "Show your PAN",
      panHint: "Under your store name",
      logo: "Show your logo",
      logoHint: "From Online store → Branding",
      qr: "Tracking QR code",
      qrHint: "Customers scan it to track the order",
      signature: "Signature line",
      signatureHint: "For signed paper invoices",
      terms: "Terms",
      termsPh: "Goods once sold can be exchanged within 7 days with the invoice.",
      footer: "Footer note",
      footerPh: "Thank you for shopping with us.",
      ird: "Adyvance invoices are simple invoices, not IRD-certified tax invoices. Each one says so.",
    },
    preview: {
      title: "Invoice preview",
      store: "Your store",
      pan: "PAN {pan}",
      invoice: "Invoice",
      number: "INV-2083-0001",
      item: "Dhaka topi × 2",
      vat: "VAT {n}%",
      total: "Total",
      signature: "Signature",
      ird: "Not an IRD-certified tax invoice.",
    },
  },
  profile: {
    lead: "Your store's name, contact details, social links, status and business details.",
    tabs: { profile: "Profile", business: "Business details", launch: "Launch checklist" },
    identity: {
      title: "Store identity",
      help: "The name and type of business customers see.",
      name: "Store name",
      nameNeeded: "Give your store a name.",
      category: "What you sell",
      categoryHelp: "Helps us suggest themes and settings.",
      pick: "Choose one",
      addLogo: "Add logo",
      changeLogo: "Change logo",
    },
    categories: {
      fashion: "Clothing and fashion",
      electronics: "Electronics and gadgets",
      beauty: "Beauty and personal care",
      grocery: "Grocery",
      home: "Home and living",
      books: "Books and stationery",
      sports: "Sports and fitness",
      kids: "Kids and toys",
      handicrafts: "Handicrafts and gifts",
      health: "Health and wellness",
      food: "Food and bakery",
      services: "Services",
      other: "Something else",
    },
    contact: {
      title: "Contact details",
      help: "Shown in your store footer, on order emails and on the contact page.",
      phonePh: "98XXXXXXXX",
      emailPh: "hello@yourstore.com",
      emailBad: "Enter an email address like name@example.com.",
      same: "Same",
    },
    social: {
      title: "Social links",
      help: "Icons in your store footer. Leave empty to hide one.",
      bad: "Enter your {network} link, starting with https://",
      names: {
        facebook: "Facebook",
        instagram: "Instagram",
        tiktok: "TikTok",
        youtube: "YouTube",
        x: "X (Twitter)",
      },
    },
    preview: {
      title: "Your store footer",
      noSocial: "Add social links to show them here.",
      copy: "Copy link",
      note: "Changes show on your store within a minute of saving.",
    },
    business: {
      complete: "Complete. Shown in your store footer and business information page.",
      addressHelp: "Your registered business address.",
      ward: "Ward {n}",
      readOnly: "Only people allowed to edit business details can change these.",
      groups: {
        business: "Business",
        businessHelp: "The name and type registered with the government.",
        registration: "Registration",
        registrationHelp: "Where and how your business is registered.",
        officer: "Complaint officer",
        officerHelp: "Who handles customer complaints (E-Commerce Act).",
        reach: "Official contact",
        reachHelp: "For legal and complaint notices.",
      },
      // D-068 store verification (ORG-02), on the same page as the disclosure.
      verify: {
        title: "Store verification",
        lead: "Adyvance checks these details and documents before payouts. Business name, type, PAN, registration number, phone and address come from the sections above.",
        verifiedTitle: "Verification completed",
        verifiedBody:
          "Your store is verified. The blue tick shows next to your store and your name.",
        submittedTitle: "Under review",
        submittedBody: "Adyvance is checking your details. You'll see the result here.",
        needsInfoTitle: "Adyvance needs more information",
        rejectedTitle: "Verification not approved",
        steps: { details: "Details", review: "Review", verified: "Verified" },
        lockedShared:
          "Locked while verification is under review or done. Use Request change to edit.",
        requestChange: "Request change",
        requestChangeConfirm:
          "Edit your verification details? Your store loses the blue tick until Adyvance approves the changes.",
        reopened: "You can edit your details now. Submit again when you're done.",
        submit: "Submit for verification",
        submitted: "Sent to Adyvance for verification",
        saveFirst: "Save your changes first",
        missing: "Still needed: {items}",
        ownerOnly: "Only an Owner can see and send store verification.",
        bank: {
          title: "Bank account (business)",
          help: "Where Adyvance sends your payouts. Use the business's own account.",
          name: "Bank name",
          account: "Account number",
          accountHelp: "Stored encrypted. Only the last 4 digits are shown later.",
          accountSaved: "Saved account ending {last4}. Type a new number to change it.",
          accountName: "Account name",
          branch: "Branch",
        },
        docs: {
          title: "Registration documents",
          help: "A clear photo or PDF of each. JPG, PNG or PDF, up to 10 MB.",
          registration: "Registration certificate",
          pan: "PAN / VAT certificate",
          other: "Other document",
          addOther: "Add another document",
          upload: "Upload",
          replace: "Replace",
          remove: "Remove",
          uploading: "Uploading…",
          uploaded: "Document uploaded",
          removed: "Document removed",
          open: "Open",
          wrongType: "Use a JPG, PNG or PDF file.",
          tooBig: "This file is over 10 MB.",
        },
        agreement: {
          title: "Adyvance agreement",
          help: "Download the agreement, sign it and upload the signed copy. Optional.",
          download: "Download agreement",
          upload: "Upload signed agreement",
          replace: "Upload a new signed copy",
          notSubmitted: "Agreement not submitted",
          submittedOn: "Signed copy uploaded {date}",
          noTemplate: "Adyvance hasn't added the agreement yet.",
          saved: "Signed agreement uploaded",
        },
        fields: {
          entity_type: "Business type",
          registered_name: "Registered business name",
          pan_vat: "PAN number",
          registered_address: "Registered address",
          business_phone: "Business phone",
          bank_name: "Bank name",
          bank_account: "Account number",
          bank_account_name: "Account name",
          bank_branch: "Branch",
          registration_certificate: "Registration certificate",
          pan_vat_certificate: "PAN / VAT certificate",
        },
      },
    },
    launch: {
      progress: "Ready",
      done: "Done",
      doneHint: "Steps finished",
      left: "Left",
      ready: "Ready to open",
      help: "Finish these to open your store. Start with the highlighted step.",
    },
  },
  notify: {
    lead: "Which alerts you get, the messages customers receive and the email address your store sends from.",
    tabs: { alerts: "Your alerts", messages: "Customer messages" },
    allOn: "Turn all on",
    allOff: "Turn all off",
    digestHelp: "A short summary of sales and anything that needs you.",
    pushNote:
      "Push works per device and browser. Turn it on again on each phone or computer you use.",
    messagesHelp: "Pick a message to edit it.",
    editedMark: "* edited by you. Everything else uses our wording.",
    channel: "Channel",
    insert: "Insert:",
    restore: "Use our wording",
    restored: "Back to our wording",
    restoreTitle: "Go back to our wording?",
    restoreBody: "Your version of this message is replaced with the default.",
    leaveEdit: "Discard your changes to this message?",
    smsFrom: "SMS from your store",
    when: {
      order_created: "Sent when a customer places an order.",
      order_confirmed: "Sent when you confirm the order.",
      order_shipped: "Sent when the parcel leaves with the courier.",
      order_delivered: "Sent when the courier marks it delivered.",
      order_cancelled: "Sent when an order is cancelled.",
      payment_verified: "Sent when an online payment is confirmed.",
      payment_rejected: "Sent when a payment can't be confirmed.",
    },
    vars: {
      store_name: "Store name",
      customer_name: "Customer name",
      order_number: "Order number",
      order_total: "Order total",
      amount_due: "Amount due",
      track_url: "Tracking link",
      payment_method: "Payment method",
    },
    sending: {
      title: "Sending address",
      help: "Emails to customers come from your own domain once it is verified.",
      default:
        "Until then, emails come from Adyvance on your store's behalf, with your store name.",
      domain: "Your domain",
      domainPh: "yourstore.com",
      removeTitle: "Stop sending from {name}?",
      removeBody: "Emails go back to the Adyvance address until you add a domain again.",
    },
    forward: {
      aliasPh: "sales@yourstore.com",
      toPh: "you@gmail.com",
      removeTitle: "Stop forwarding {name}?",
    },
    smtp: {
      title: "Your own email server (SMTP)",
      help: "Sending through another email provider.",
      body: "Not offered: store emails go through Cloudflare Email Sending, so no server password is ever stored here. Add your domain above instead.",
    },
  },
  locations: {
    tiles: {
      total: "Locations",
      totalHint: "{n} switched off",
      outlets: "Shops and outlets",
      outletsHint: "Where you sell in person (POS)",
      warehouses: "Warehouses",
      warehousesHint: "Where you keep stock",
      online: "Ship online orders",
      onlineHint: "Their stock shows on your store",
    },
    edit: "Edit location",
    nameNeeded: "Give the location a name.",
    namePh: "Thamel shop",
    codeTaken: "Another location uses this code.",
    codeFixed: "Printed on receipts; can't change.",
    shipsOnline: "Ships online orders",
    counterOnly: "Counter only",
    switchOff: "Switch off",
    switchOn: "Switch on",
    switchOffTitle: "Switch off {name}?",
    switchOffBody:
      "It stops showing in POS and stock counts. Its stock history is kept, and you can switch it on again.",
    note: "New stock goes to the default location unless you pick another. Every shop with a POS register needs its own location.",
    kindHelp: {
      outlet: "A shop or counter where you sell in person.",
      warehouse: "A store room or godown that ships orders.",
      online_pool: "Stock kept only for online orders.",
    },
  },
  installation: {
    tiles: {
      services: "Services on",
      servicesHint: "{n} switched off",
      products: "Products using them",
      productsHint: "Buyers can add the service at checkout",
      areas: "Priced areas",
      areasHint: "Provinces, districts and municipalities",
      from: "Cheapest",
      fromHint: "The lowest price you charge",
    },
    noPrice: "No price yet",
    elsewhere: "Elsewhere {price}",
  },
  help: {
    lead: "Guides for every part of Adyvance, and a direct line to our team.",
    hero: "How can we help?",
    heroSub: "Search the guides or ask our team.",
    search: "Search the guides",
    searchPh: "For example: eSewa, delivery charge, coupon",
    ask: "Ask our team",
    guides: "Guides: {n}",
    allGuides: "All guides",
    allTopics: "All topics",
    guidesHelp: "Guides: {n}",
    noMatch: "No guide matches that.",
    askInstead: "Ask our team instead",
    watch: "Watch the video",
    updated: "Updated {date}",
    stillStuck: "Still stuck? Ask us",
    topics: {
      start: "Getting started",
      products: "Products",
      orders: "Orders",
      delivery: "Delivery",
      payments: "Payments",
      promotions: "Offers",
      store: "Online store",
      customers: "Customers",
      settings: "Settings",
    },
    tickets: {
      title: "Your questions",
      help: "Questions and problems you sent us.",
      open: "{n} waiting or answered",
      new: "New",
      empty: "Nothing yet. Ask anything, we read every message.",
      sla: "We aim to answer within a day, and within 4 hours for problems that stop your sales.",
      states: { open: "Waiting for us", answered: "Answered", closed: "Closed" },
    },
    category: "About",
    categories: {
      question: "A question",
      problem: "A problem",
      billing: "Billing",
      feature: "An idea",
    },
    subject: "In one line",
    subjectPh: {
      question: "How do I add a size chart?",
      problem: "Orders don't show the delivery charge",
      billing: "Question about my plan",
      feature: "Let customers pay in instalments",
    },
    subjectNeeded: "Write at least 3 characters.",
    body: "Tell us more",
    bodyHelp: "What you tried and what happened. Never send passwords or card numbers.",
    bodyNeeded: "Tell us a little more.",
    context: "We attach your store and the page you are on, so we can look straight away.",
    send: "Send",
    sent: "Sent. We'll reply here and let you know in your notifications.",
    you: "You",
    author: { staff: "Adyvance team", system: "Adyvance" },
    replyPh: "Write a reply",
    closed: "This conversation is closed. Ask again if you need more help.",
  },
  org: {
    lead: "{org}: the business behind your stores, its verification and its activity log.",
    tabs: { business: "Business", verification: "Verification", activity: "Activity log" },
    renamed: "Business name saved",
    copy: "Copy",
    tiles: {
      stores: "Stores",
      storesHint: "In this business",
      verification: "Verification",
      verificationHint: "Needed for payouts",
      since: "On Adyvance since",
      sinceHint: "When the business was created",
      plan: "Plan",
      planHint: "See Plan and billing",
    },
    name: {
      title: "Business name",
      help: "Shown to your team and on Adyvance invoices. Your store name is set in Store profile.",
      label: "Name",
    },
    facts: {
      address: "Admin address",
      id: "Business ID",
      referral: "Referral code",
      status: "Status",
    },
    lifecycle: { active: "Active", closed: "Closed" },
    stores: {
      title: "Stores",
      help: "Every store in this business shares the team, plan and billing.",
    },
    close: {
      title: "Close this business",
      help: "Stops every store, removes the team's access and ends billing. Only the owner can do this.",
      action: "Close business",
      warning: "This can't be undone from here.",
      what: {
        stores: "Every store goes offline and stops taking orders.",
        data: "Your data is kept for the period the law requires, then deleted.",
        billing: "Your plan ends; unused credit is handled by the refund policy.",
      },
      reason: "Why are you leaving? (optional)",
      type: "Type {name} to confirm",
      done: "The business is closed.",
    },
    kyc: {
      steps: { draft: "Fill in", submitted: "Under review", approved: "Verified" },
      next: {
        draft: "Fill in the details, save, then submit for review.",
        submitted: "We are checking your details. This usually takes 1 to 2 working days.",
        needs_info: "We need a little more. See the note, update and submit again.",
        approved: "Your business is verified.",
        rejected: "Not approved. See the note or ask us in Help centre.",
      },
      business: "Business",
      businessHelp: "As registered with the government.",
      bank: "Payout bank account",
      bankHelp: "Where Adyvance pays out your online payments.",
      docsHelp: "Registration certificate, PAN certificate and the signatory's ID.",
      saveFirst: "Save your changes first.",
      locked:
        "Details can't be changed while under review or after approval. Ask us in Help centre to change them.",
    },
  },
  team: {
    tiles: {
      members: "Team members",
      membersHint: "People who can sign in",
      invites: "Invitations",
      invitesHint: "Waiting to be accepted",
      seats: "Seats",
      roles: "Roles in use",
      rolesHint: "Each person has one role",
    },
    roles: {
      title: "Roles and what they can do",
      help: "The bar shows how much each role can do.",
      people: "People: {n}",
      note: "Give each person the smallest role they need. You can change a role any time from the list above.",
      about: {
        owner: "Everything, including billing, the team and closing the business.",
        manager: "Runs the store day to day: products, orders, customers and offers.",
        order_manager: "Creates, updates and cancels orders, and answers customer messages.",
        cashier: "Sells at the shop counter (POS), creates orders and issues invoices.",
        inventory: "Edits products and keeps stock and transfers up to date.",
        marketing: "Edits and publishes the online store, products and customer messages.",
        accountant: "Sees payments and money, checks manual payments and records entries.",
        viewer: "Looks at products, orders, customers and reports, and changes nothing.",
        custom: "A role you made with your own choice of permissions.",
      },
    },
  },
} as const;
